import React, { useEffect } from "react";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Flex } from "@amzn/stencil-react-components/layout";
import { useChecklistArb } from "../../hooks/use-arb";
import {
  getCurrentPage,
  getDocStatusCards,
  getDownloadableDocs,
  getFiles,
  getIsActionButtonEnabled,
  getIsCountryWithDownloadableContentDocUpload,
  getSelectedFileType,
  removeAllFiles,
  setActivePage,
  setDownloadableDocs,
  toggleSubmitButton,
  updateDocStatusCardsFromEDM,
} from "../../reduxStore/slices/docUploadSlice";
import { DOC_UPLOAD_PAGE_STATE, EDM_REVIEW_STATUS } from "../../utility/enums/common";
import "./index.style.scss";
import { useAppDispatch, useAppSelector } from "../../reduxStore/reduxHooks";
import { useCommitDocumentsMutation, usePreassignedLinkMutation } from "../../reduxStore/api/apiSlice";
import {
  getValidEdmPreCommitPayload,
  isCandidateAlreadyDownloadNecessaryDocUpload,
  validateDownloadDocs,
} from "../../helpers/utils";
import { parseDocStatusCards, parsePresignedURLResponse, setDocLoadingState } from "./helpers";
import { useHistory, useParams } from "react-router-dom";
import { DebounceButton } from "../common/DebounceButton";
import { getConfigData } from "../../reduxStore/slices/configSlice";
import { ActionPath } from "../../utility/types/common";
import { useActionPath } from "./hooks/useActionPath";
import AppLoader from "../common/AppLoader";
import { dispatchOnDev } from "../../utility/dev-env-helpers";

export const ActionButtons = () => {
  const bundle = useChecklistArb();
  const history = useHistory();
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();
  const actionPath = useActionPath();

  const dispatch = useAppDispatch();
  const isActionButtonEnabled = useAppSelector(getIsActionButtonEnabled);
  const { candidateData } = useAppSelector(getConfigData);
  const selectedFile = useAppSelector(getSelectedFileType);
  const currentPage = useAppSelector(getCurrentPage);
  const docStatusCards = useAppSelector(getDocStatusCards);
  const files = useAppSelector(getFiles);
  const isCountryWithDownloadableContent = useAppSelector(getIsCountryWithDownloadableContentDocUpload);
  const downloadableDocs = useAppSelector(getDownloadableDocs);

  const [commitDocumentsToEDM] = useCommitDocumentsMutation();
  const [
    getPreassignedLink,
    { data: preassignedLinkResponse, isSuccess: successPreassignedLink, isError: errorPreassignedLink },
  ] = usePreassignedLinkMutation();

  const isEligibleToCommitDocsToEDM =
    docStatusCards.length > 0 && docStatusCards.filter((doc) => !doc.markForDeletion).length > 0;

  useEffect(() => {
    switch (currentPage) {
      case DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE:
        if (isEligibleToCommitDocsToEDM) {
          dispatch(toggleSubmitButton({ isEnabled: true }));
        }
        break;

      case DOC_UPLOAD_PAGE_STATE.DOWNLOAD_DOCUMENTS:
        if (isCandidateAlreadyDownloadNecessaryDocUpload(applicationId)) {
          dispatch(toggleSubmitButton({ isEnabled: true }));
        }
        break;

      default:
        dispatch(toggleSubmitButton({ isEnabled: false }));
    }
  }, [currentPage, isEligibleToCommitDocsToEDM, applicationId]);

  useEffect(() => {
    if (successPreassignedLink && preassignedLinkResponse) {
      dispatch(
        updateDocStatusCardsFromEDM(
          parseDocStatusCards({
            files,
            preassignedLinkResponse,
            status: EDM_REVIEW_STATUS.APPROVED,
            actionPath,
          })
        )
      );
    }
    if (errorPreassignedLink) {
      dispatch(
        updateDocStatusCardsFromEDM(
          parseDocStatusCards({
            files,
            preassignedLinkResponse,
            status: EDM_REVIEW_STATUS.ERROR,
            actionPath,
          })
        )
      );
    }
  }, [preassignedLinkResponse, errorPreassignedLink, successPreassignedLink]);

  const triggerAction = () => {
    switch (currentPage) {
      case DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE: {
        const cardsRelatedToPage = docStatusCards.filter((card) => card.actionPath === actionPath);
        const documents = getValidEdmPreCommitPayload(cardsRelatedToPage);
        commitDocumentsToEDM({
          applicationId,
          candidateGlobalId: candidateData?.candidateGlobalId as string,
          documents,
        });
        break;
      }
      case DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD:
        dispatch(updateDocStatusCardsFromEDM(setDocLoadingState({ files, actionPath: ActionPath.UPLOAD_DOCUMENT })));
        getPreassignedLink({
          applicationId,
          candidateGlobalId: candidateData?.candidateGlobalId as string,
          documentTypeId: selectedFile ? selectedFile?.documentType : "", // Get the ID from dropdown when user make selection
          files: parsePresignedURLResponse({ files }),
        });
        // call presigned url to save on select file to upload & update docsWidget on Redux
        dispatch(setActivePage({ page: DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE }));
        dispatch(removeAllFiles());
        break;
      case DOC_UPLOAD_PAGE_STATE.DOWNLOAD_DOCUMENTS: {
        const { downloadableDocs: validatedDocs, hasError } = validateDownloadDocs({
          bundle,
          docs: downloadableDocs.docs,
          hasError: downloadableDocs.showErrorBanner,
        });

        dispatch(
          setDownloadableDocs({
            showErrorBanner: hasError,
            downloadableDocs: validatedDocs,
          })
        );

        if (!hasError) dispatch(setActivePage({ page: DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE }));
      }
    }
  };

  const handleCancelAction = () => {
    switch (currentPage) {
      case DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD:
        dispatch(setActivePage({ page: DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE }));
        break;

      case DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE:
        if (isCountryWithDownloadableContent) {
          dispatch(setActivePage({ page: DOC_UPLOAD_PAGE_STATE.DOWNLOAD_DOCUMENTS }));
        } else {
          history.push(`/checklist/${requisitionId}/${applicationId}`);
        }
        break;

      case DOC_UPLOAD_PAGE_STATE.DOWNLOAD_DOCUMENTS:
        history.push(`/checklist/${requisitionId}/${applicationId}`);
        break;

      default:
        dispatchOnDev(() => {
          console.error("Unhandled doc upload page state");
        });
    }
  };

  const getActionLabel = () => {
    switch (currentPage) {
      case DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD:
        return bundle.getMessage("checklist-General-Upload-Button", "Upload");
      case DOC_UPLOAD_PAGE_STATE.DOWNLOAD_DOCUMENTS:
        return bundle.getMessage("Checklist-docUpload-button", "Next");
      case DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE:
        return bundle.getMessage("Checklist-Common-Save", "Save");
      default:
        return "N/A"; //Not a valid state
    }
  };

  return (
    <Flex flexDirection="row" gridGap="S300" className="flyout-container">
      <Button variant={ButtonVariant.Tertiary} onClick={handleCancelAction}>
        {bundle.getMessage("Checklist-Common-Cancel", "Cancel")}
      </Button>
      <DebounceButton
        variant={ButtonVariant.Primary}
        disabled={!isActionButtonEnabled}
        data-testid={`save-disabled=${!isActionButtonEnabled}`}
        onClick={triggerAction}
      >
        {getActionLabel()}
      </DebounceButton>
    </Flex>
  );
};
